/**
 * The v2 text-unit engine: runs one block per letter/word/line with stagger.
 *
 * Splitting happens at render time (`blockAnimatedContent` wraps units in
 * `<span data-ht-unit="i">` spans); this module only does the timing math —
 * given a unit count, it computes each unit's start offset and evaluates the
 * block on the unit's own local clock. Every reference ad uses this: the
 * Jitter exports bake ~35ms/char staggers into per-glyph layers; we compute
 * the same offsets live.
 */
import type {
  AnimationBlock,
  TextUnitSpec,
} from "@hightouch/lib/ad-studio/html-editor/animation-blocks";
import {
  type BlockEvalContext,
  type BlockStyle,
  blockStylesForLayer,
} from "./block-style-at";
import { easedProgress } from "./easings";

const fnv1a = (text: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h = Math.imul(h ^ text.charCodeAt(i), 0x01000193);
  }
  return h >>> 0;
};

/** `ranks[unit]` is the position of `unit` in `order`. */
const ranksOf = (order: number[]): number[] => {
  const ranks = new Array<number>(order.length);
  order.forEach((unit, rank) => {
    ranks[unit] = rank;
  });
  return ranks;
};

/**
 * The stagger rank of each unit (0 animates first). `orderIndex[i]` is the
 * rank of unit `i` in document order.
 */
export function unitOrderIndices(
  spec: Pick<TextUnitSpec, "order">,
  count: number,
  seed = "",
): number[] {
  const indices = Array.from({ length: count }, (_, i) => i);
  switch (spec.order) {
    case "forward":
      return indices;
    case "reverse":
      return indices.map((i) => count - 1 - i);
    case "center-out": {
      // Rank units by distance from the center; ties break toward the left
      // unit so the cascade reads symmetrically.
      const center = (count - 1) / 2;
      return ranksOf(
        [...indices].sort(
          (a, b) => Math.abs(a - center) - Math.abs(b - center) || a - b,
        ),
      );
    }
    case "random":
      // Same seed, same order, same pixels on every render context.
      return ranksOf(
        [...indices].sort(
          (a, b) => fnv1a(`${seed}:${a}`) - fnv1a(`${seed}:${b}`) || a - b,
        ),
      );
  }
}

/**
 * Per-unit start offsets in frames. Without smoothing, rank r starts at
 * `r * delay`; smoothing redistributes the ranks along the curve while
 * preserving the total stagger window `(count - 1) * delay`.
 */
export function unitStartOffsets(
  spec: TextUnitSpec,
  count: number,
  seed = "",
): number[] {
  if (count <= 0) return [];
  const ranks = unitOrderIndices(spec, count, seed);
  const window = (count - 1) * spec.delay;
  if (count === 1 || window === 0) return ranks.map(() => 0);
  return ranks.map((rank) => {
    const position = rank / (count - 1);
    const eased = spec.smoothing
      ? easedProgress(spec.smoothing, position)
      : position;
    return eased * window;
  });
}

/**
 * Evaluate a layer's unit-staggered blocks for every unit: unit `i` sees the
 * same blocks with each block's start shifted by that block's stagger offset
 * for the unit, so per-property chaining still holds within a unit. Returns
 * one style (or null = identity) per unit, in document order. `seed` fixes
 * the `random` order; callers pass the layer id so the blocks on one layer
 * agree on it.
 */
export function unitStylesForLayer(
  blocks: readonly AnimationBlock[],
  count: number,
  frame: number,
  context: BlockEvalContext,
  seed = "",
): (BlockStyle | null)[] {
  const offsets = blocks.map((block) =>
    block.text ? unitStartOffsets(block.text, count, seed) : undefined,
  );
  return Array.from({ length: count }, (_, unit) =>
    blockStylesForLayer(
      blocks.map((block, i) => {
        const offset = offsets[i]?.[unit];
        return offset ? { ...block, start: block.start + offset } : block;
      }),
      frame,
      context,
    ),
  );
}
